<template>
    <div class="container form-container p-4">
        <div class="text-3xl mb-4 pb-2 font-light flex items-end justify-between" v-if="!start_tfa_process">
            <div data-cy="user-settings-security-title">
                {{ $t('user.security.title.change_password') }}
            </div>
            <a-button
                data-cy="change-password-button"
                class="button-submit"
                @click="handleSubmit"
                :loading="processing"
            >
                {{ $t('user.security.submit') }}
            </a-button>
        </div>
        <form @change="isFormDirty" @submit.prevent="handleSubmit" data-cy="form-container" v-if="!start_tfa_process">
            <p class="text-center text-default-red p-2" v-if="validation.message">
                {{ validation.message }}
            </p>
            <div class="form-section" data-cy="section-security-general">
                <div class="section-body-np">
                    <div data-cy="field-old-password" class="input-container mb-2 md:w-2/5">
                        <ui-label>
                            {{ $t('user.forms.change_password.old_password') }}
                            <span class="text-default-red">*</span>
                        </ui-label>
                        <ui-input
                            type="password"
                            :class="{ 'form-error': v$.password_change.old_password.$error }"
                            v-model="v$.password_change.old_password.$model"
                        />
                        <div
                            data-cy="field-old-password-required-error"
                            :class="{ 'text-default-red': v$.password_change.old_password.$error }"
                            v-if="
                                v$.password_change.old_password.required.$invalid &&
                                v$.password_change.old_password.$error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <validation-errors :errors="validation.errors.old_password" v-if="validation.errors" />
                    </div>
                </div>
                <div class="section-body-np">
                    <div data-cy="field-new-password" class="input-container mb-2 md:w-2/5">
                        <ui-label>
                            {{ $t('user.forms.change_password.new_password') }}
                            <span class="text-default-red">*</span>
                        </ui-label>
                        <ui-input
                            type="password"
                            :class="{ 'form-error': v$.password_change.password.$error }"
                            v-model="v$.password_change.password.$model"
                        />
                        <div
                            data-cy="field-new-password-required-error"
                            :class="{ 'text-default-red': v$.password_change.password.$error }"
                            v-if="v$.password_change.password.required.$invalid && v$.password_change.password.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <div
                            data-cy="field-new-password-min-length-error"
                            :class="{ 'text-default-red': v$.password_change.password.$error }"
                            v-if="v$.password_change.password.minLength.$invalid && v$.password_change.password.$error"
                        >
                            {{
                                $t('validation.min.string', {
                                    attribute: 'Password',
                                    min: app_config.password_minimum_length,
                                })
                            }}
                        </div>
                        <div
                            data-cy="field-new-password-numbers-error"
                            :class="{ 'text-default-red': v$.password_change.password.$error }"
                            v-if="
                                v$.password_change.password.includeNumber.$invalid && v$.password_change.password.$error
                            "
                        >
                            {{ $t('validation.include_numbers', { attribute: 'Password' }) }}
                        </div>
                        <div
                            data-cy="field-new-password-symbols-error"
                            :class="{ 'text-default-red': v$.password_change.password.$error }"
                            v-if="
                                v$.password_change.password.includeSymbol.$invalid && v$.password_change.password.$error
                            "
                        >
                            {{ $t('validation.include_symbols', { attribute: 'Password' }) }}
                        </div>
                        <validation-errors :errors="validation.errors.password" v-if="validation.errors" />
                    </div>
                </div>
                <div class="section-body-np">
                    <div data-cy="field-confirm-password" class="input-container md:w-2/5">
                        <ui-label>
                            {{ $t('user.forms.change_password.confirm_password') }}
                            <span class="text-default-red">*</span>
                        </ui-label>
                        <ui-input
                            type="password"
                            :class="{ 'form-error': v$.password_change.password_confirmation.$error }"
                            v-model="v$.password_change.password_confirmation.$model"
                        />
                        <div
                            data-cy="field-confirm-password-required-error"
                            :class="{ 'text-default-red': v$.password_change.password_confirmation.$error }"
                            v-if="
                                v$.password_change.password_confirmation.required.$invalid &&
                                v$.password_change.password_confirmation.$error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <div
                            data-cy="field-confirm-password-match-error"
                            :class="{ 'text-default-red': v$.password_change.password_confirmation.$error }"
                            v-if="
                                !v$.password_change.password_confirmation.required.$invalid &&
                                v$.password_change.password_confirmation.sameAsPassword.$invalid &&
                                v$.password_change.password_confirmation.$error
                            "
                        >
                            {{ $t('validation.confirmed', { attribute: 'Password' }) }}
                        </div>
                        <validation-errors :errors="validation.errors.password_confirmation" v-if="validation.errors" />
                    </div>
                </div>
                <div class="section-body">
                    <div class="text-gray-600 text-xs italic" data-cy="password-config-info">
                        <span>
                            {{ $t('user.forms.change_password.criteria') }}
                        </span>
                        <ul class="list-disc px-4">
                            <li v-if="app_config.password_minimum_length > 0" data-cy="password-min-length">
                                {{
                                    $t('validation.min.string', {
                                        attribute: 'Password',
                                        min: app_config.password_minimum_length,
                                    })
                                }}
                            </li>
                            <li v-if="app_config.password_include_numbers" data-cy="password-include-numbers">
                                {{ $t('validation.include_numbers', { attribute: 'Password' }) }}
                            </li>
                            <li v-if="app_config.password_include_symbols" data-cy="password-include-symbols">
                                {{ $t('validation.include_symbols', { attribute: 'Password' }) }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </form>
        <div class="text-3xl mb-4 pb-2 font-light flex items-end justify-between">
            <div data-cy="user-settings-security-tfa-title">
                {{ $t('user.security.title.tfa') }}
            </div>
            <a-button
                v-if="tfaState && !tfaEnforced"
                data-cy="tfa-disable-button"
                :class="security_tfa_button_color"
                @mouseover="displayTFAActionText"
                @mouseleave="displayTFAStaticText"
                @click="handleDisableTFA"
                :loading="tfa_processing"
            >
                {{ security_tfa_button_text }}
            </a-button>
        </div>
        <div v-if="tfaState && !start_tfa_process">
            <ul class="list-reset flex flex-col" data-cy="tfa-enabled-state">
                <li
                    class="rounded-t-md relative -mb-px block border p-4 border-grey font-medium bg-gray-100"
                    data-cy="tfa-methods-title"
                >
                    {{ $t('user.security.tfa.details.methods_title') }}
                </li>
                <li class="relative -mb-px block border p-4 border-grey" data-cy="tfa-app-method">
                    <span class="inline-block w-1/2">
                        {{ $t('user.security.tfa.details.auth_app') }}
                    </span>
                    <span class="inline-block w-1/2 float-right text-right" v-if="tfaType == 'app'">
                        {{ $t('user.security.tfa.details.auth_app_set') }}
                    </span>
                    <span class="inline-block w-1/2 float-right text-right" v-if="tfaType != 'app'">
                        {{ $t('user.security.tfa.details.auth_app_not_set') }}
                    </span>
                </li>
                <li class="relative -mb-px block border p-4 border-grey" data-cy="tfa-email-method">
                    <span class="inline-block w-1/2">
                        {{ $t('user.security.tfa.details.email') }}
                    </span>
                    <span class="inline-block w-1/2 float-right text-right" v-if="tfaType == 'email'">
                        {{ $t('user.security.tfa.details.email_set') }}
                    </span>
                    <span class="inline-block w-1/2 float-right text-right" v-if="tfaType != 'email'">
                        {{ $t('user.security.tfa.details.email_not_set') }}
                    </span>
                </li>
                <li
                    class="relative -mb-px block border p-4 border-grey font-medium bg-gray-100"
                    data-cy="tfa-recovery-title"
                >
                    {{ $t('user.security.tfa.details.recovery_title') }}
                </li>
                <li class="rounded-b-md relative -mb-px block border p-4 border-grey" data-cy="tfa-recovery-method">
                    <span class="inline-block w-1/2">
                        {{ $t('user.security.tfa.details.recovery_codes') }}
                    </span>
                    <span class="inline-block w-1/2 float-right text-right">
                        <a-button @click="downloadRecoveryCodes" size="small" data-cy="tfa-download-codes-button">
                            {{ $t('user.security.tfa.download') }}
                        </a-button>
                    </span>
                </li>
            </ul>
        </div>

        <div v-if="!tfaState && !start_tfa_process" data-cy="template-enable-tfa">
            <a-result
                :title="$t('user.security.tfa.enable_title')"
                :sub-title="$t('user.security.tfa.enable_sub_title')"
            >
                <template #icon>
                    <LockOutlined class="icon-color" />
                </template>
                <template #extra>
                    <a-button type="primary" @click="handleEnableTFA" data-cy="tfa-enable-button">
                        {{ $t('user.security.tfa.enable_button') }}
                    </a-button>
                </template>
            </a-result>
        </div>
        <div class="flex flex-wrap" v-if="start_tfa_process">
            <div class="w-full md:w-2/5 lg:3/5">
                <a-steps direction="vertical" :current="tfa_process">
                    <a-step title="Choose Setup" />
                    <a-step title="Authentication Verification" />
                    <a-step title="Save your Recovery Codes" />
                    <a-step title="Two-Factor Authentication Activated" />
                </a-steps>
            </div>
            <div class="w-full md:w-3/5 lg:4/5">
                <div v-if="tfa_process == 0" class="step-template-override" data-cy="tfa_step_1">
                    <tfa-step-1 :tfa_processing="tfa_processing" @cancelTFA="cancelTFA" @chooseTFA="chooseTFA" />
                </div>
                <div v-if="tfa_process == 1" class="step-template-override" data-cy="tfa_step_2">
                    <tfa-step-2
                        :tfa_option="tfa_option"
                        :tfa_qrcode_url="tfa_qrcode_url"
                        :tfa_processing="tfa_processing"
                        @cancelTFA="cancelTFA"
                        @submitTFAOption="submitTFAOption"
                    />
                </div>
                <div v-if="tfa_process == 2" data-cy="tfa_step_3">
                    <tfa-step-3
                        :tfa_recovery_codes="tfa_recovery_codes"
                        :tfa_processing="tfa_processing"
                        @downloadRecoveryCodes="downloadRecoveryCodes"
                        @enableTFA="enableTFA"
                    />
                </div>
                <div v-if="tfa_process == 3" data-cy="tfa_step_4">
                    <tfa-step-4 @cancelTFA="cancelTFA" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import UiCheckbox from '@/components/ui/Checkbox.vue';
import UiInput from '@/components/ui/Input.vue';
import UiLabel from '@/components/ui/Label.vue';
import TfaStep1 from '@/components/tfa/Step1.vue';
import TfaStep2 from '@/components/tfa/Step2.vue';
import TfaStep3 from '@/components/tfa/Step3.vue';
import TfaStep4 from '@/components/tfa/Step4.vue';

import ValidationErrors from '@/components/ui/ValidationErrors.vue';

import { useVuelidate } from '@vuelidate/core';
import { required, sameAs } from '@vuelidate/validators';

import store from '@/store';
import FileManager from '@/utils/file.js';
import { LockOutlined } from '@ant-design/icons-vue';

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    components: {
        UiCheckbox,
        UiInput,
        UiLabel,
        TfaStep1,
        TfaStep2,
        TfaStep3,
        TfaStep4,
        ValidationErrors,
        LockOutlined,
    },
    computed: {
        tfaState() {
            return this.$store.state.user.is_tfa_enabled ? true : false;
        },
        tfaType() {
            return this.$store.state.user.tfa_type;
        },
        tfaEnforced() {
            return this.$store.state.user.is_association_tfa_enabled || this.$store.state.user.is_company_tfa_enabled
                ? true
                : false;
        },
    },
    data() {
        return {
            app_config: {},
            security_tfa_button_text: this.$t('user.security.tfa.enabled'),
            security_tfa_button_color: 'enable_button',
            start_tfa_process: false,
            tfa_processing: false,
            tfa_process: 0,
            tfa_option: 'app',
            tfa_qrcode_url: '',
            tfa_code: '',
            tfa_recovery_codes: [],
        };
    },
    mounted() {
        this.$http
            .all([
                this.$http.post('/api/users/get-config', {
                    id: this.password_change.user_id,
                }),
            ])
            .then(
                this.$http.spread((config) => {
                    let config_data = {
                        ...config.data,
                        password_include_numbers: config.data.password_include_numbers ? true : false,
                        password_include_symbols: config.data.password_include_symbols ? true : false,
                    };

                    this.app_config = config_data;
                }),
            );
    },
    props: {
        password_change: Object,
        processing: {
            type: Boolean,
            default: false,
        },
        validation: Object,
    },
    validations: {
        password_change: {
            user_id: {},
            old_password: { required },
            password: {
                required,
                minLength(value) {
                    if (this.app_config.password_minimum_length > 0) {
                        return value.length >= this.app_config.password_minimum_length;
                    }

                    return true;
                },
                includeNumber(value) {
                    if (this.app_config.password_include_numbers) {
                        return /[0-9]/.test(value);
                    }

                    return true;
                },
                includeSymbol(value) {
                    if (this.app_config.password_include_symbols) {
                        return /[#?!@$%^&*-]/.test(value);
                    }

                    return true;
                },
            },
            password_confirmation: {
                required,
                sameAsPassword(value) {
                    return value === this.password_change.password;
                },
            },
        },
    },
    methods: {
        isFormDirty() {
            this.$emit('is-form-dirty', this.v$.$anyDirty);
        },
        async handleSubmit() {
            if (!(await this.v$.$validate())) return false;

            let change_password = {
                user_id: this.password_change.user_id,
                old_password: this.password_change.old_password,
                password: this.password_change.password,
                password_confirmation: this.password_change.password_confirmation,
            };

            this.$emit('formSubmit', {
                tab: 'Security',
                data: change_password,
            });
        },
        displayTFAActionText() {
            this.security_tfa_button_text = this.$t('user.security.tfa.disable');
            this.security_tfa_button_color = 'disable_button';
        },
        displayTFAStaticText() {
            this.security_tfa_button_text = this.$t('user.security.tfa.enabled');
            this.security_tfa_button_color = 'enable_button';
        },
        handleDisableTFA() {
            let that = this;

            this.$confirm({
                title: this.$t('user.security.tfa.confirm.disable'),
                content: '',
                onOk() {
                    that.tfa_processing = true;

                    that.$http
                        .post('/api/tfa/disable')
                        .then((response) => {
                            if (response.data.status == 'success') {
                                store.dispatch('user/updateTFAProcessed', 0);
                                store.dispatch('user/updateTFA', { tfa_type: '', is_tfa_enabled: 0 });
                                that.tfa_process = 0;
                            } else {
                                that.$notification.error({
                                    message: that.$t('user.notifications.error.tfa_disable'),
                                });
                            }
                        })
                        .catch((error) => {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVSTFAD01' }),
                            });
                        })
                        .finally(() => {
                            that.tfa_processing = false;
                        });
                },
            });
        },
        handleEnableTFA() {
            this.start_tfa_process = true;
        },
        cancelTFA() {
            this.start_tfa_process = false;
            this.tfa_process = 0;
        },
        chooseTFA(tfa_option) {
            this.tfa_processing = true;
            this.tfa_option = tfa_option;

            switch (this.tfa_option) {
                case 'app':
                    this.$http
                        .post('/api/tfa/generate/url')
                        .then((response) => {
                            if (response.data.status == 'success') {
                                this.tfa_process = 1;
                                this.tfa_qrcode_url = response.data.url;
                            } else {
                                this.$notification.error({
                                    message: this.$t('user.notifications.error.tfa_generate_qrcode'),
                                });
                            }
                        })
                        .catch((error) => {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVSTFAQR01' }),
                            });
                        })
                        .finally(() => {
                            this.tfa_processing = false;
                        });
                    break;
                case 'email':
                    this.$http
                        .post('/api/tfa/generate/email')
                        .then((response) => {
                            if (response.data.status == 'success') {
                                this.tfa_process = 1;
                            } else {
                                this.$notification.error({
                                    message: this.$t('user.notifications.error.tfa_generate_email'),
                                });
                            }
                        })
                        .catch((error) => {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVSTFAE01' }),
                            });
                        })
                        .finally(() => {
                            this.tfa_processing = false;
                        });
                    break;
            }
        },
        submitTFAOption(tfa_code) {
            this.tfa_code = tfa_code;

            if (this.tfa_code == '') {
                this.$notification.error({
                    message: this.$t('user.notifications.error.tfa_code_required'),
                });
                return false;
            }

            this.tfa_processing = true;

            switch (this.tfa_option) {
                case 'app':
                    this.$http
                        .post('/api/tfa/verify/url', { code: this.tfa_code })
                        .then((response) => {
                            if (response.data.valid) {
                                this.tfa_code = '';
                                this.tfa_recovery_codes = response.data.recovery_codes;
                                this.tfa_process = 2;
                            } else {
                                this.$notification.error({
                                    message: this.$t('user.notifications.error.tfa_verify_code'),
                                });
                            }
                        })
                        .catch((error) => {
                            if (error.response.status == 403) {
                                this.$notification.error({
                                    message: this.$t('globals.global.notifications.error.403_mod'),
                                });
                            } else {
                                this.$notification.error({
                                    message: this.$t('globals.global.notifications.error.any', { code: 'CVSTFAVU01' }),
                                });
                            }
                        })
                        .finally(() => {
                            this.tfa_processing = false;
                        });
                    break;
                case 'email':
                    this.$http
                        .post('/api/tfa/verify/email', { code: this.tfa_code })
                        .then((response) => {
                            if (response.data.valid) {
                                this.tfa_code = '';
                                this.tfa_recovery_codes = response.data.recovery_codes;
                                this.tfa_process = 2;
                            } else {
                                this.$notification.error({
                                    message: this.$t('user.notifications.error.tfa_verify_code'),
                                });
                            }
                        })
                        .catch((error) => {
                            if (error.response.status == 403) {
                                this.$notification.error({
                                    message: this.$t('globals.global.notifications.error.403_mod'),
                                });
                            } else {
                                this.$notification.error({
                                    message: this.$t('globals.global.notifications.error.any', { code: 'CVSTFAVE01' }),
                                });
                            }
                        })
                        .finally(() => {
                            this.tfa_processing = false;
                        });
                    break;
            }
        },
        downloadRecoveryCodes() {
            this.$http
                .post('/api/tfa/download-codes')
                .then((response) => {
                    if (response.headers['content-type'] == 'text/html; charset=UTF-8') {
                        FileManager.download(response);
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVSTFADC01' }),
                    });
                });
        },
        enableTFA() {
            this.tfa_processing = true;

            this.$http
                .post('/api/tfa/enable', { type: this.tfa_option })
                .then((response) => {
                    if (response.data.status == 'success') {
                        store.dispatch('user/updateTFAProcessed', 1);
                        store.dispatch('user/updateTFA', { tfa_type: this.tfa_option, is_tfa_enabled: 1 });
                        this.tfa_process = 3;
                    } else {
                        this.$notification.error({
                            message: this.$t('user.notifications.error.tfa_enable'),
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVSTFAE01' }),
                        });
                    }
                })
                .finally(() => {
                    this.tfa_processing = false;
                });
        },
    },
};
</script>
